import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button, Card, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary - catches JavaScript errors in child components and displays a fallback UI.
 * Use to prevent the entire app from crashing when a component throws.
 */
class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallbackUI
          error={this.state.error}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackUIProps {
  error: Error;
  onRetry: () => void;
  onReload: () => void;
}

const ErrorFallbackUI: React.FC<ErrorFallbackUIProps> = ({
  error,
  onRetry,
  onReload,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center bg-light"
      style={{ padding: "1rem" }}
    >
      <Container>
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <div className="mb-4">
              <i
                className="fe-alert-triangle text-danger"
                style={{ fontSize: "4rem" }}
              />
            </div>
            <h2 className="mb-3 text-dark">
              {t("Something went wrong")}
            </h2>
            <p className="text-muted mb-4">
              {t("An unexpected error occurred. Please try again or refresh the page.")}
            </p>
            {/* Détail technique affiché aussi en prod pour faciliter le diagnostic sur mobile. */}
            <pre
              className="text-start bg-dark text-light p-3 rounded mb-4"
              style={{ fontSize: "0.75rem", maxHeight: "200px", overflow: "auto" }}
            >
              {error.name}: {error.message}
            </pre>
            <div className="d-flex gap-2 justify-content-center flex-wrap">
              <Button variant="primary" onClick={onRetry}>
                {t("Try again")}
              </Button>
              <Button variant="outline-secondary" onClick={onReload}>
                {t("Reload page")}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ErrorBoundaryClass;
